import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Typography, ButtonBase } from '@mui/material';
import Tooltip from 'components/interface/tooltip/Tooltip';
import Symbol from 'components/Symbol';

const useStyles = makeStyles<
    { color?: string | null },
    'selected' | 'disabled' | 'hover'
>()((theme, { color }, classes) => ({
    base: {
        'color': theme.palette.text.secondary,
        'position': 'relative',
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'flex-start',
        'textAlign': 'start',
        'cursor': 'pointer',
        'padding': theme.spacing(1, 2),
        'minHeight': '32px',
        'borderRadius': theme.shape.borderRadius,
        'userSelect': 'none',
        'columnGap': theme.spacing(1),
        [`&.${classes.selected}`]: {
            color: theme.palette.primary[900],
            background: theme.palette.primary[50],
        },
        [`&.${classes.disabled}`]: {
            color: theme.palette.text.hint,
        },
        [`&.${classes.hover}`]: {
            color: theme.palette.grey[900],
            background: theme.palette.grey[100],
        },
        '&:hover': {
            color: theme.palette.grey[900],
            background: theme.palette.grey[100],
        },

        '&::before': color && {
            position: 'absolute',
            content: '""',
            display: 'block',
            width: '8px',
            height: '100%',
            background: color,
            borderRadius: '4px 0 0 4px',
            left: 0,
            top: 0,
        },
    },
    selected: {},
    disabled: {},
    hover: {},
    underline: {
        textDecoration: 'underline',
    },
    text: {
        fontWeight: '500',
    },
}));

interface MenuItemProps {
    children: React.ReactNode;
    classes?: string;
    className?: string;
    onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    disabled?: boolean;
    selected?: boolean;
    hover?: boolean;
    icon?: React.ReactNode;
    symbol?: string;
    underline?: boolean;
    tooltipText?: React.ReactNode;
    color?: string | null;
}

const MenuItem = React.forwardRef<HTMLButtonElement, MenuItemProps>(
    (
        {
            children,
            classes: classesProps,
            className: classNameProps,
            onClick,
            onMouseEnter,
            disabled,
            selected,
            hover,
            icon,
            symbol,
            underline,
            tooltipText,
            color,
        }: MenuItemProps,
        ref
    ) => {
        const { classes, cx } = useStyles({ color: color });

        const button = () => (
            <ButtonBase
                ref={ref}
                className={cx(classes.base, classesProps, classNameProps, {
                    [classes.selected]: selected,
                    [classes.disabled]: disabled,
                    [classes.hover]: hover && !selected,
                    [classes.underline]: underline,
                })}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                disabled={disabled}>
                {icon}
                {symbol && (
                    <Symbol
                        symbol={symbol}
                        size='md'
                        color='textPrimary'
                    />
                )}
                {typeof children === 'string' ||
                (Array.isArray(children) &&
                    children.every(i => typeof i === 'string')) ? (
                    <Typography
                        className={classes.text}
                        variant='body2'
                        color='inherit'>
                        {children}
                    </Typography>
                ) : (
                    children
                )}
            </ButtonBase>
        );

        return tooltipText ? (
            <Tooltip title={tooltipText} placement='right'>
                <div>{button()}</div>
            </Tooltip>
        ) : (
            button()
        );
    }
);

MenuItem.displayName = 'MenuItem';

export default MenuItem;
